/**
 * Internal Invoice Lines Redux Slice
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
export interface InternalInvoiceLine {
  id: number;
  internal_invoice_id: number;
  description: string | null;
  service_type: string | null;
  quantity: number | null;
  unit_price: number | null;
  line_total: number | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
interface InternalInvoiceLinesState extends FilteredState<InternalInvoiceLine> {
  selectedLine: InternalInvoiceLine | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  groupedByInvoice: Record<number, InternalInvoiceLine[]>;
}
const initialState: InternalInvoiceLinesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 100,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'internal_invoice_id',
  sortDirection: 'desc',
  searchTerm: null,
  selectedLine: null,
  creating: false,
  updating: false,
  deleting: false,
  groupedByInvoice: {},
};
export const fetchInternalInvoiceLines = createAsyncThunk<{ data: InternalInvoiceLine[]; totalCount: number }, any>(
  'internalInvoiceLines/fetchLines',
  async (params: any) => {
    throw new Error('Not implemented yet');
  }
);
export const fetchInternalInvoiceLineById = createAsyncThunk('internalInvoiceLines/fetchLineById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createInternalInvoiceLine = createAsyncThunk('internalInvoiceLines/createLine', async (line: Partial<InternalInvoiceLine>) => {
  throw new Error('Not implemented yet');
});
export const updateInternalInvoiceLine = createAsyncThunk('internalInvoiceLines/updateLine', async ({ id, data }: { id: number; data: Partial<InternalInvoiceLine> }) => {
  throw new Error('Not implemented yet');
});
export const deleteInternalInvoiceLine = createAsyncThunk('internalInvoiceLines/deleteLine', async (id: number) => {
  throw new Error('Not implemented yet');
});
const internalInvoiceLinesSlice = createSlice({
  name: 'internalInvoiceLines',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectLine: (state, action: PayloadAction<InternalInvoiceLine | null>) => {
      state.selectedLine = action.payload;
    },
    clearSelection: (state) => {
      state.selectedLine = null;
    },
    groupByInvoice: (state) => {
      const grouped: Record<number, InternalInvoiceLine[]> = {};
      state.items.forEach((line) => {
        if (!grouped[line.internal_invoice_id]) {
          grouped[line.internal_invoice_id] = [];
        }
        grouped[line.internal_invoice_id].push(line);
      });
      state.groupedByInvoice = grouped;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInternalInvoiceLines.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInternalInvoiceLines.fulfilled, (state, action: PayloadAction<{ data: InternalInvoiceLine[]; totalCount: number }>) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
        const grouped: Record<number, InternalInvoiceLine[]> = {};
        action.payload.data.forEach((line: InternalInvoiceLine) => {
          if (!grouped[line.internal_invoice_id]) {
            grouped[line.internal_invoice_id] = [];
          }
          grouped[line.internal_invoice_id].push(line);
        });
        state.groupedByInvoice = grouped;
      })
      .addCase(fetchInternalInvoiceLines.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar líneas de factura interna';
      });
  },
});
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectLine, clearSelection, groupByInvoice } = internalInvoiceLinesSlice.actions;
export default internalInvoiceLinesSlice.reducer;
